import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../api';
import './Auth.css';

function LoginPage() {
  const [values, setValues] = useState({ correo: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async event => {
    event.preventDefault();
    if (loading) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_URL}/login`, {
        correo: values.correo.trim().toLowerCase(),
        password: values.password
      });
      if (response.data?.status !== 'Success' || !response.data?.token) {
        throw new Error('Respuesta de autenticación inválida');
      }
      sessionStorage.setItem('authToken', response.data.token);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('usuario', response.data.user);
      localStorage.setItem('rol', response.data.rol);
      navigate(response.data.rol === 'admin' ? '/admin' : '/', { replace: true });
    } catch (requestError) {
      const status = requestError.response?.status;
      if (status === 429) {
        setError('Demasiados intentos. Espera unos minutos antes de reintentar.');
      } else if (status === 401 || status === 400) {
        setError('Correo o contraseña incorrectos.');
      } else {
        setError('Error interno del servidor. Por favor, contacta al administrador.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="auth-wrapper">
      <section className="auth-card" aria-labelledby="login-title">
        {/* Encabezado y Marca */}
        <header className="auth-header">
          <h1 id="login-title" className="auth-logo-title">
            LEOTECH<span className="auth-logo-dot"></span>
          </h1>
          <p className="auth-subtitle">Ingresa a tu cuenta</p>
        </header>

        {/* Validación Visual de Error */}
        {error && (
          <div className="auth-alert auth-alert-error" role="alert">
            <svg className="auth-alert-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {/* Formulario de Login */}
        <form onSubmit={handleSubmit} noValidate>
          <div className="auth-form-group">
            <label htmlFor="login-email" className="auth-label">Correo electrónico</label>
            <div className="auth-input-wrapper">
              <span className="auth-input-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="20" height="16" x="2" y="4" rx="2" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
              </span>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                className="auth-input"
                placeholder=""
                value={values.correo}
                onChange={event => setValues(prev => ({ ...prev, correo: event.target.value }))}
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="auth-form-group">
            <label htmlFor="login-password" className="auth-label">Contraseña</label>
            <div className="auth-input-wrapper">
              <span className="auth-input-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </span>
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                className="auth-input"
                placeholder=""
                value={values.password}
                onChange={event => setValues(prev => ({ ...prev, password: event.target.value }))}
                required
                disabled={loading}
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setShowPassword(prev => !prev)}
                aria-label={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                    <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                    <line x1="2" y1="2" x2="22" y2="22" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Botón Principal Destacado en Rojo (#e62117) con Flecha */}
          <button type="submit" className="auth-btn-primary" disabled={loading}>
            {loading ? (
              <>
                <span className="auth-spinner" />
                <span>INGRESANDO…</span>
              </>
            ) : (
              <>
                <span>INGRESAR</span>
                <svg className="auth-btn-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </>
            )}
          </button>

          {/* Enlace "¿Olvidaste tu contraseña?" */}
          <div className="auth-forgot-row">
            <Link to="/registro" className="auth-forgot-link">
              ¿Olvidaste tu contraseña?
            </Link>
          </div>

          {/* Botón Claro para "Crear cuenta nueva" */}
          <Link to="/registro" className="auth-btn-secondary">
            Crear cuenta nueva
          </Link>

          {/* Volver a la tienda */}
          <div className="auth-back-container">
            <Link to="/" className="auth-back-link">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              <span>Volver a la tienda</span>
            </Link>
          </div>
        </form>
      </section>
    </main>
  );
}

export default LoginPage;
